import { useState, useRef, useMemo, useCallback } from 'react';
import { EditorInstance } from 'novel';
import { DecorationSet, Decoration } from '@tiptap/pm/view';

interface UseDecorationsOptions {
    editorInstance: EditorInstance | null;
    isAiProcessing?: boolean;
    isEditorFocused?: boolean;
}

interface ParagraphPosition {
    from: number;
    to: number;
}

interface UseDecorationsReturn {
    decorations: DecorationSet;
    activeParagraphs: ParagraphPosition[];
    preservedSelection: { from: number; to: number } | null;
    setPreservedSelection: (sel: { from: number; to: number } | null) => void;
    trackActiveParagraph: (editor: EditorInstance) => void;
}

/**
 * Hook to manage editor decorations internally.
 * 
 * Handles:
 * - Active paragraph highlight (shows current editing position)
 * - Multi-paragraph selection highlight
 * - Preserved selection/cursor during AI processing
 * 
 * PERFORMANCE: Uses refs for selection tracking to avoid re-renders during drag selection.
 * Only triggers state updates when the active paragraphs actually change.
 */
export function useDecorations({
    editorInstance,
    isAiProcessing = false,
    isEditorFocused = true,
}: UseDecorationsOptions): UseDecorationsReturn {
    // Active paragraphs tracking - supports multiple paragraphs in selection
    const [activeParagraphs, setActiveParagraphs] = useState<ParagraphPosition[]>([]);

    // Preserved selection (for AI processing)
    const [preservedSelection, setPreservedSelection] = useState<{ from: number; to: number } | null>(null);

    // Use refs for selection tracking to avoid re-renders during selection
    const lastParagraphsRef = useRef<string>('');

    // Track active paragraphs - finds all paragraphs within selection range
    const trackActiveParagraph = useCallback((editor: EditorInstance) => {
        const { from, to } = editor.state.selection;
        const doc = editor.state.doc;
        const paragraphs: ParagraphPosition[] = [];

        // Walk through the document to find all text blocks in selection range
        doc.nodesBetween(from, to, (node, pos) => {
            if (node.isTextblock) {
                const nodeStart = pos;
                const nodeEnd = pos + node.nodeSize;

                // Check if this paragraph overlaps with selection
                if (nodeEnd > from && nodeStart < to) {
                    paragraphs.push({
                        from: pos + 1,  // Content start (inside the node)
                        to: pos + node.nodeSize - 1  // Content end
                    });
                }
            }
            return true; // Continue traversal
        });

        // Create a signature to compare - only update if paragraphs changed
        const signature = paragraphs.map(p => `${p.from}-${p.to}`).join(',');

        if (signature !== lastParagraphsRef.current) {
            lastParagraphsRef.current = signature;
            setActiveParagraphs(paragraphs);
        }
    }, []);

    // Build decorations - highlights all active paragraphs
    const decorations = useMemo(() => {
        if (!editorInstance) {
            return DecorationSet.empty;
        }

        const { doc } = editorInstance.state;
        const decorationList: Decoration[] = [];

        // Active paragraph decorations - highlight all paragraphs in selection
        for (const paragraph of activeParagraphs) {
            const { from, to } = paragraph;
            try {
                // Validate positions are within document bounds
                if (from > 0 && to <= doc.content.size) {
                    const paragraphDecoration = Decoration.node(from - 1, to + 1, {
                        class: 'active-paragraph-highlight'
                    });
                    decorationList.push(paragraphDecoration);
                }
            } catch {
                // Ignore if positions are invalid
            }
        }

        // Preserved selection/cursor decorations:
        // Show when AI is processing OR when editor is not focused (to show selection while typing in chat)
        const shouldShowPreservedSelection = (isAiProcessing || !isEditorFocused) && preservedSelection;
        if (shouldShowPreservedSelection) {
            const { from, to } = preservedSelection;

            try {
                if (from === to) {
                    // Cursor position indicator
                    const cursorDecoration = Decoration.widget(from, () => {
                        const cursor = document.createElement('span');
                        cursor.className = 'preserved-cursor';
                        return cursor;
                    });
                    decorationList.push(cursorDecoration);
                } else {
                    // Selection highlight
                    const selectionDecoration = Decoration.inline(from, to, {
                        class: 'preserved-selection'
                    });
                    decorationList.push(selectionDecoration);
                }
            } catch {
                // Ignore if positions are invalid
            }
        }

        if (decorationList.length > 0) {
            return DecorationSet.create(doc, decorationList);
        }
        return DecorationSet.empty;
    }, [editorInstance, activeParagraphs, preservedSelection, isAiProcessing, isEditorFocused]);

    return {
        decorations,
        activeParagraphs,
        preservedSelection,
        setPreservedSelection,
        trackActiveParagraph,
    };
}

